import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";
import ProductCard from "../components/products/ProductCard";
import CreateProductModal from "../components/products/CreateProductModal";
import UpdateProductModal from "../components/products/UpdateProductModal";
import AppCircularProgress from "../components/common/AppCircularProgress";

import { usePermissions } from "../hooks/usePermissions";
import {
  getProducts,
  deleteProduct,
  resetProductServerStatus,
} from "../redux/actions/productActions";
import PageError from "../components/common/PageError";

const ProductsPage = () => {
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions();

  const {
    products,
    loadingProducts,
    errorLoadingProducts,
    serverStatus,
    loadingProductDelete,
    loadingProductUpdate,
    loadingProductCreate,
    errorCreatingProduct,
    errorUpdatingProduct,
  } = useSelector((state) => state.product);

  const [searchTerm, setSearchTerm] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const canCreate = hasPermission("product:create");

  const fetchProducts = useCallback(() => {
    dispatch(getProducts());
  }, [dispatch]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Frontend Filtering
  const filteredProducts = useMemo(() => {
    if (!products) return [];
    if (!searchTerm.trim()) return products;

    const query = searchTerm.toLowerCase();
    return products.filter(
      (p) =>
        p.name.toLowerCase().includes(query) ||
        p.eTimsItemCode?.toLowerCase().includes(query) ||
        p.eTimsNarration?.toLowerCase().includes(query),
    );
  }, [products, searchTerm]);

  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;
    if (operation === "create" && status === 201) setIsCreateModalOpen(false);
    if (operation === "update" && status === 200) setIsUpdateModalOpen(false);
    if (operation === "delete" && status === 200) setIsDeleteModalOpen(false);
    dispatch(resetProductServerStatus());
  }, [serverStatus, dispatch]);

  const renderContent = () => {
    if (loadingProducts) return <AppCircularProgress />;
    if (errorLoadingProducts)
      return <PageError error={errorLoadingProducts} onClick={fetchProducts} />;

    if (filteredProducts.length === 0)
      return (
        <Box textAlign="center" mt={5}>
          <Typography color="text.secondary">
            {searchTerm
              ? `No products match "${searchTerm}"`
              : "No products registered."}
          </Typography>
        </Box>
      );

    return (
      <Grid container spacing={3}>
        {filteredProducts.map((p) => (
          <Grid item key={p.id} xs={12} sm={6} md={4} lg={3}>
            <ProductCard
              product={p}
              onEdit={() => {
                setSelectedProduct(p);
                setIsUpdateModalOpen(true);
              }}
              onDelete={() => {
                setSelectedProduct(p);
                setIsDeleteModalOpen(true);
              }}
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <PageContainer
      title="Product Catalog"
      subtitle="Manage your licensing products and eTIMS tax configurations."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsCreateModalOpen(true)}
          >
            Register Product
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search by name or eTIMS code..."
      />
      {renderContent()}

      {isCreateModalOpen && (
        <CreateProductModal
          open={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          loading={loadingProductCreate}
          error={errorCreatingProduct}
        />
      )}
      {isUpdateModalOpen && selectedProduct && (
        <UpdateProductModal
          open={isUpdateModalOpen}
          onClose={() => setIsUpdateModalOpen(false)}
          product={selectedProduct}
          loading={loadingProductUpdate}
          error={errorUpdatingProduct}
        />
      )}
      {isDeleteModalOpen && selectedProduct && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={() => dispatch(deleteProduct(selectedProduct.id))}
          title="Delete Product"
          message={`Are you sure you want to delete "${selectedProduct.name}"?`}
          loading={loadingProductDelete}
        />
      )}
    </PageContainer>
  );
};

export default ProductsPage;
